/**
 * One answer to "what is this runner's finish time?", for every page.
 *
 * Results, ResultsEditor, VenueDisplay, RunnerDetailsModal and the Excel export
 * each derived this independently, and each did it by asking whether a mat
 * reading existed. That equated "has checkpoints" with "has a result", which is
 * true for chip timing and false for buzzer-timed events (ninja/obstacle, speed
 * climbing) — those store an elapsed time and no crossings at all. Every page
 * therefore had to be fixed separately, and each new page repeated the bug.
 *
 * Resolution order, most authoritative first:
 *   1. finishDuration   — computed by the API, or recorded directly by buzzer
 *   2. gunTime/netTime  — MYLAPS stores an elapsed string here
 *   3. finish mat       — derive from the crossing instant minus the start
 */

/** Milliseconds from any stored time value: Timestamp, Date, ISO, epoch. */
const toMs = (value) => {
  if (value == null) return NaN;
  if (typeof value === "number") return value;
  if (value instanceof Date) return value.getTime();
  if (typeof value.toDate === "function") return value.toDate().getTime();
  if (typeof value.seconds === "number") return value.seconds * 1000;
  if (typeof value._seconds === "number") return value._seconds * 1000;
  const parsed = new Date(value).getTime();
  return Number.isNaN(parsed) ? NaN : parsed;
};

/** True when a string already reads as an elapsed time (hh:mm:ss[.SSS]). */
const isDurationString = (value) =>
  typeof value === "string" && /^\d{1,3}:\d{2}:\d{2}(\.\d+)?$/.test(value.trim());

const pad = (n, width = 2) => String(n).padStart(width, "0");

/** Milliseconds → hh:mm:ss.SSS */
export const msToDuration = (ms) => {
  if (!Number.isFinite(ms) || ms < 0) return null;
  const total = Math.round(ms);
  const s = Math.floor(total / 1000);
  return `${pad(Math.floor(s / 3600))}:${pad(Math.floor((s % 3600) / 60))}:${pad(s % 60)}.${pad(total % 1000, 3)}`;
};

/** hh:mm:ss[.SSS] → milliseconds, or NaN. */
export const durationToMs = (value) => {
  if (!isDurationString(value)) return NaN;
  const [h, m, rest] = value.trim().split(":");
  const [s, frac = "0"] = rest.split(".");
  return (
    (Number(h) * 3600 + Number(m) * 60 + Number(s)) * 1000 +
    Number(frac.padEnd(3, "0").slice(0, 3))
  );
};

/**
 * The runner's elapsed finish time as hh:mm:ss.SSS, or null when they have none.
 *
 * `category` is optional and only used for the mat fallback, where a duration
 * has to be derived from the crossing instant.
 */
export const finishDurationOf = (runner = {}, category = null) => {
  if (isDurationString(runner.finishDuration)) return runner.finishDuration.trim();
  if (isDurationString(runner.recordedDuration)) return runner.recordedDuration.trim();

  // A lap-counted category has no finish time to fall back to. Its gunTime
  // holds a single lap, not a race total, and treating that as a finish would
  // report a 24-hour runner as having finished in 1:17 — the standing there is
  // the lap count, which the API expresses by leaving finishDuration null.
  if (category?.isLooping) return null;

  if (isDurationString(runner.gunTime)) return runner.gunTime.trim();
  if (isDurationString(runner.netTime)) return runner.netTime.trim();
  if (isDurationString(runner.finishTime)) return runner.finishTime.trim();

  // Mat fallback: finish crossing minus the runner's own start, or the gun.
  const finishMs = toMs(
    runner.checkpoints?.finish?.time ?? runner.finishMatTime ?? runner.finishTime,
  );
  if (!Number.isFinite(finishMs)) return null;

  const ownStartMs = toMs(runner.checkpoints?.start?.time);
  const startMs = category?.individualStart
    ? ownStartMs
    : Number.isFinite(ownStartMs)
      ? ownStartMs
      : toMs(category?.startTime);
  if (!Number.isFinite(startMs)) return null;

  const penaltyMs = (Number(runner.penaltySeconds) || 0) * 1000;
  return msToDuration(finishMs + penaltyMs - startMs);
};

/**
 * True when the runner has completed the course — by any timing method.
 * This is what pages should ask instead of inspecting `checkpoints`.
 */
export const hasFinished = (runner = {}, category = null) =>
  finishDurationOf(runner, category) !== null;

/**
 * True when a category's times are shown to the hundredth of a second.
 *
 * Chip timing stores milliseconds that mean nothing to a runner, so every page
 * drops the fraction. A short hand-timed format (pool races, sprints) is the
 * opposite: there the hundredths are what separates one place from the next,
 * and dropping them shows a tie that the ranking does not have.
 */
export const showsHundredths = (category) => category?.showHundredths === true;

/**
 * Display form of an elapsed time string.
 *
 * Whole seconds by default. With showHundredths the fraction is kept to two
 * digits (truncated, so the time the operator typed reads back unchanged) and
 * a zero hour group is dropped: 00:01:23.450 → 01:23.45. Anything that is not
 * a plain duration (COT labels, clock times, "-") passes through untouched
 * apart from the default fraction strip.
 */
export const displayElapsed = (value, category = null) => {
  if (typeof value !== "string") return value;
  const text = value.trim();
  if (!showsHundredths(category)) return text.replace(/\.\d+$/, "");
  const match = text.match(/^(\d{1,3}):(\d{2}):(\d{2})(?:\.(\d+))?$/);
  if (!match) return text;
  const [, h, m, s, frac = ""] = match;
  const hundredths = frac.padEnd(2, "0").slice(0, 2);
  return Number(h) === 0 ? `${m}:${s}.${hundredths}` : `${h}:${m}:${s}.${hundredths}`;
};

/** Display form: see displayElapsed, "-" when there is no finish. */
export const finishDisplay = (runner = {}, category = null) => {
  const duration = finishDurationOf(runner, category);
  return duration ? displayElapsed(duration, category) : "-";
};
